﻿import React, { useState, useMemo, useCallback, useEffect } from "react";
import { TooltipHost, DirectionalHint, SearchBox, IconButton } from "@fluentui/react";
import { TbTableOptions } from "react-icons/tb";
import { MdExpandLess, MdExpandMore, MdContentCopy, MdSaveAlt, MdFileDownload, MdCheck, MdClose, MdEdit, MdEditOff } from "react-icons/md";
import { BiChevronRight, BiChevronDown } from "react-icons/bi";
import { BsFileEarmarkText, BsListUl, BsHash, BsToggleOn } from "react-icons/bs";
import { AiOutlineNumber } from "react-icons/ai";
import { toast } from "react-toastify";
import { parseJsonToProperties, hasHtmlTagsStrict, prepUcpData } from "./../../util_glob";
import "./JsonViewerInteractive.css";
import { BiCollapseVertical } from "react-icons/bi";
import { BiExpandVertical } from "react-icons/bi";
import { dpaSearchStyles } from "../../styles_glob";
import { CiSaveUp1 } from "react-icons/ci";
import { downloadJsonAsCsv } from "./jsonToCsvExport";
import { gselectedgroup } from "../../interfaces";


interface JsonViewerInteractiveProps {
    jsonString?: string;
    title?: string;
    aiid?: string;
    fieldorder?: string[];
    onFieldOrderChange?: (order: string[], aiid: string) => void;
    onApplyFieldOrderToDocType?: (fieldorder: string[]) => void;
    sgp?: gselectedgroup; // permission level from selected group, used to determine if "Apply to Doc Type" button should be shown
    onDataHighlight?: (data: string) => void;
    editable?: boolean;
    onContentChange?: (updatedData: any, aiid: string) => void;
}







const setDeepValue = (obj: any, path: string[], newValue: any): any => {
    if (path.length === 0) return newValue;
    const [head, ...rest] = path;
    if (Array.isArray(obj)) {
        const idx = parseInt(head, 10);
        const copy = [...obj];
        copy[idx] = setDeepValue(copy[idx], rest, newValue);
        return copy;
    }
    return { ...obj, [head]: setDeepValue(obj?.[head], rest, newValue) };
};

const coerceValue = (input: string, originalValue: any): any => {
    if (typeof originalValue === "number") { const n = Number(input); return isNaN(n) ? input : n; }
    if (typeof originalValue === "boolean") { const l = input.toLowerCase(); return l === "true" || l === "1"; }
    return input;
};

const JsonViewerInteractive: React.FC<JsonViewerInteractiveProps> = ({ jsonString, title = "Data", aiid, fieldorder, onFieldOrderChange, onApplyFieldOrderToDocType, sgp, onDataHighlight, editable, onContentChange }) => {
    const [collapsedFields, setCollapsedFields] = useState<{ [key: string]: boolean }>({});
    const [searchTerm, setSearchTerm] = useState("");
    const [draggedKey, setDraggedKey] = useState<string | null>(null);

    // edit mode state
    const [editModeEnabled, setEditModeEnabled] = useState(false);
    const [editingPath, setEditingPath] = useState<string | null>(null);
    const [editValue, setEditValue] = useState("");
    const [editableData, setEditableData] = useState<any>({});

    const gridData = useMemo(() => {
        if (!jsonString) return {};
        try {
            return typeof jsonString === "object" ? jsonString : JSON.parse(jsonString);
        } catch (error) {
            console.error("‹errr‾json‾viewer‾parse›", error);
            toast.error("Invalid JSON input");
            return {};
        }
    }, [jsonString]);

    const displayData = useMemo(() => prepUcpData(gridData), [gridData]);

    // sync editableData with displayData when source changes (and not actively editing)
    useEffect(() => {
        if (!editingPath) setEditableData(displayData);
    }, [displayData]);



    // get ordered keys - use fieldorder if provided, otherwise use object keys
    const orderedKeys = useMemo(() => {
        const allKeys = Object.keys(displayData).filter(k => k !== "structured_data");
        if (fieldorder && fieldorder.length > 0) {
            const orderedFromProp = fieldorder.filter(k => allKeys.includes(k));
            const remaining = allKeys.filter(k => !fieldorder.includes(k));
            return [...orderedFromProp, ...remaining];
        }
        return allKeys;
    }, [displayData, fieldorder]);

    const handleDragStart = (key: string) => setDraggedKey(key);
    const handleDragEnd = () => setDraggedKey(null);
    const handleDragOver = (e: React.DragEvent) => e.preventDefault();
    const handleDrop = (targetKey: string) => {
        if (!draggedKey || draggedKey === targetKey || !aiid) return;
        const newOrder = [...orderedKeys];
        const dragIdx = newOrder.indexOf(draggedKey);
        const dropIdx = newOrder.indexOf(targetKey);
        newOrder.splice(dragIdx, 1);
        newOrder.splice(dropIdx, 0, draggedKey);
        onFieldOrderChange?.(newOrder, aiid);
        setDraggedKey(null);
    };

    const getSchemeIntoClipboard = (json: any) => {
        const properties = parseJsonToProperties(json);
        const propertiesJson = JSON.stringify(properties, null, 2);
        navigator.clipboard
            .writeText(propertiesJson)
            .then(() => {
                toast.success("Content Scheme copied to clipboard!", { autoClose: 1500 });
            })
            .catch(error => {
                console.error("Failed to copy scheme:", error);
                toast.error("Failed to copy scheme.");
            });
    };

    const getDataIntoClipboard = (json: any) => {
        const jsonString = typeof json === "string" ? json : JSON.stringify(json, null, 2);
        navigator.clipboard
            .writeText(jsonString)
            .then(() => {
                toast.success("Content copied to clipboard!", { autoClose: 1500 });
            })
            .catch(error => {
                console.error("Failed to copy content:", error);
                toast.error("Failed to copy content.");
            });
    };

    const copyValueToClipboard = (value: any, key: string) => {
        const textValue = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
        const isHtml = typeof value === "string" && hasHtmlTagsStrict(value);

        const doCopy = isHtml
            ? (() => {
                  // write both text/html and text/plain so rich-text editors (Outlook, Word, etc.) get the formatted version while plain-text editors get a clean fallback.
                  const htmlBlob = new Blob([textValue], { type: "text/html" });
                  // extract plain text by letting the browser render the HTML, same as select-and-copy
                  const tmp = document.createElement("div");
                  tmp.innerHTML = textValue;
                  const plainText = tmp.textContent || tmp.innerText || textValue;
                  const textBlob = new Blob([plainText], { type: "text/plain" });
                  return navigator.clipboard.write([new ClipboardItem({ "text/html": htmlBlob, "text/plain": textBlob })]);
              })()
            : navigator.clipboard.writeText(textValue);

        doCopy
            .then(() => {
                import.meta.env.DEV && console.log(`‹copi›`);
            })
            .catch(error => {
                console.error("Failed to copy value:", error);
                toast.error("Failed to copy value.");
            });
    };

    const toggleCollapse = useCallback((path: string) => {
        setCollapsedFields(prev => ({
            ...prev,
            [path]: !prev[path]
        }));
    }, []);

    const expandAll = () => {
        setCollapsedFields({});
    };

    const collapseAll = () => {
        const allPaths: { [key: string]: boolean } = {};
        const getAllPaths = (obj: any, parentPath: string[] = []) => {
            Object.entries(obj).forEach(([key, value]) => {
                const currentPath = [...parentPath, key].join("__");
                if (typeof value === "object" && value !== null && Object.keys(value).length > 0) {
                    allPaths[currentPath] = true;
                    getAllPaths(value, [...parentPath, key]);
                }
            });
        };
        getAllPaths(displayData);
        setCollapsedFields(allPaths);
    };

    const expandAllArrayItems = (arrayPath: string[], arrayLength: number) => {
        setCollapsedFields(prev => {
            const updated = { ...prev };
            for (let i = 0; i < arrayLength; i++) {
                const itemPath = [...arrayPath, String(i)].join("__");
                delete updated[itemPath];
            }
            return updated;
        });
    };

    const collapseAllArrayItems = (arrayPath: string[], arrayLength: number) => {
        setCollapsedFields(prev => {
            const updated = { ...prev };
            for (let i = 0; i < arrayLength; i++) {
                const itemPath = [...arrayPath, String(i)].join("__");
                updated[itemPath] = true;
            }
            return updated;
        });
    };

    const getTypeIcon = (value: any) => {
        if (Array.isArray(value)) return <BsListUl className="type-icon array-icon" />;
        if (typeof value === "string") return <BsFileEarmarkText className="type-icon string-icon" />;
        if (typeof value === "number") return <AiOutlineNumber className="type-icon number-icon" />;
        if (typeof value === "boolean") return <BsToggleOn className="type-icon boolean-icon" />;
        if (typeof value === "object" && value !== null) return <BsHash className="type-icon object-icon" />;
        return null;
    };

    const getPreviewText = (value: any): string => {
        if (Array.isArray(value)) return `[${value.length} items]`;
        if (typeof value === "object" && value !== null) return `{${Object.keys(value).length} fields}`;
        return String(value);
    };

    const normalizeForSearch = (str: string): string => str.toLowerCase().replace(/[^a-zа-я0-9]/g, "");

    const deepMatchesSearch = (value: any, searchNorm: string): boolean => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) {
            return value.some(item => deepMatchesSearch(item, searchNorm));
        }
        if (typeof value === "object") {
            return Object.entries(value).some(([k, v]) =>
                normalizeForSearch(k).includes(searchNorm) || deepMatchesSearch(v, searchNorm)
            );
        }
        return normalizeForSearch(String(value)).includes(searchNorm);
    };

    const matchesSearch = (key: string, value: any): boolean => {
        if (!searchTerm) return true;
        const searchNorm = normalizeForSearch(searchTerm);
        if (normalizeForSearch(key).includes(searchNorm)) return true;
        return deepMatchesSearch(value, searchNorm);
    };

    // --- inline edit helpers ---
    const startEdit = (pathKey: string, currentValue: any) => {
        setEditingPath(pathKey);
        setEditValue(String(currentValue));
    };

    const cancelEdit = () => { setEditingPath(null); setEditValue(""); };

    const commitEdit = (pathKey: string, fullPath: string[], originalValue: any) => {
        const coerced = coerceValue(editValue, originalValue);
        const updated = setDeepValue(editableData, fullPath, coerced);
        setEditableData(updated);
        setEditingPath(null);
        setEditValue("");
        if (onContentChange && aiid) onContentChange(updated, aiid);
    };

    const renderEditableValue = (value: any, pathKey: string, fullPath: string[]): JSX.Element => {
        const isEditing = editingPath === pathKey;
        const canEdit = editable && editModeEnabled && typeof value !== "object";

        if (isEditing) {
            return (
                <div className="jvi-edit-wrapper">
                    <input
                        className="jvi-edit-input"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") commitEdit(pathKey, fullPath, value); if (e.key === "Escape") cancelEdit(); }}
                        autoFocus
                        onClick={e => e.stopPropagation()}
                    />
                    <div className="jvi-edit-actions">
                        <div className="jvi-edit-btn jvi-edit-confirm" onClick={e => { e.stopPropagation(); commitEdit(pathKey, fullPath, value); }} title="Confirm"><MdCheck size={16} /></div>
                        <div className="jvi-edit-btn jvi-edit-cancel" onClick={e => { e.stopPropagation(); cancelEdit(); }} title="Cancel"><MdClose size={16} /></div>
                    </div>
                </div>
            );
        }

        const displayVal = String(value);
        const isHtml = typeof value === "string" && hasHtmlTagsStrict(displayVal);

        if (canEdit) {
            return (
                <span className={`json-value ${typeof value} jvi-editable-value`} title={displayVal} onClick={e => { e.stopPropagation(); startEdit(pathKey, value); }}>
                    {isHtml ? <div className="json-value-html" dangerouslySetInnerHTML={{ __html: displayVal }} /> : displayVal}
                </span>
            );
        }

        // default: no edit mode — original rendering
        return (
            <span className={`json-value ${typeof value}`} title={displayVal}>
                {isHtml
                    ? <div className="json-value-html" dangerouslySetInnerHTML={{ __html: displayVal }} />
                    : <div onClick={onDataHighlight ? () => onDataHighlight(displayVal) : undefined} className={onDataHighlight ? "json-string-highlightable" : undefined}>{displayVal}</div>}
            </span>
        );
    };

    const renderArrayItem = (item: any, index: number, parentPath: string[]): JSX.Element => {
        const fullPath = [...parentPath, String(index)];
        const pathKey = fullPath.join("__");
        const isCollapsed = collapsedFields[pathKey];
        const isObject = typeof item === "object" && item !== null && !Array.isArray(item);

        if (!isObject) { // primitive array item
            return (
                <div key={pathKey} className="json-array-item-primitive">
                    {renderEditableValue(item, pathKey, fullPath)}
                </div>
            );
        }

        const entries = Object.entries(item);

        const sendHighlight = (data: string) => {
            if (onDataHighlight) onDataHighlight(data);
        }

        return (
            <div key={pathKey} className={`json-array-item ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                <div className="json-array-item-toggle" onClick={() => toggleCollapse(pathKey)} title={isCollapsed ? "Expand" : "Collapse"}>
                    {isCollapsed ? <BiChevronRight size={16} /> : <BiChevronDown size={16} />}
                </div>
                <div className="json-array-item-number">{index}</div>
                {/* preview - visible when collapsed */}
                <div className={`json-array-item-preview ${isCollapsed ? 'visible' : 'hidden'}`} onClick={() => toggleCollapse(pathKey)}>
                    <div className="json-array-item-preview-inner">
                        {entries.slice(0, 3).map(([k, v], idx) => {
                            const previewValue = getPreviewText(v);
                            return (
                                <span key={k} className="json-preview-field">
                                    <span className="json-preview-key">{k.replace(/_/g, " ")}:</span>
                                    <span className={`json-preview-value ${typeof v}`}>
                                        {previewValue.length > 30 ? previewValue.substring(0, 30) + "..." : previewValue}
                                    </span>
                                    {idx < Math.min(entries.length - 1, 2) && <span className="json-preview-separator">•</span>}
                                </span>
                            );
                        })}
                        {entries.length > 3 && <span className="json-preview-more">+{entries.length - 3} more</span>}
                    </div>
                </div>

                {/* content - visible when expanded */}
                <div className={`json-array-item-content ${isCollapsed ? 'hidden' : 'visible'}`}>
                    <div className="json-array-item-content-inner">
                        {entries.map(([childKey, childValue]) => (
                            <div key={childKey} className="json-array-item-field">
                                {typeof childValue === "object" && childValue !== null ? (
                                    renderValue(childValue, childKey, 0, fullPath)
                                ) : (
                                    <>
                                        <div className="json-field-key-wrapper">
                                            <span className="json-field-key">
                                            {getTypeIcon(childValue)}
                                                {childKey.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                                            </span>
                                        </div>
                                        {renderEditableValue(childValue, [...fullPath, childKey].join("__"), [...fullPath, childKey])}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div
                    className="json-array-item-copy"
                    onClick={(e) => {
                        e.stopPropagation();
                        copyValueToClipboard(item, `Item ${index}`);
                    }}
                    title="Copy item"
                >
                    <MdContentCopy size={13} />
                </div>
            </div>
        );
    };

    const renderValue = (value: any, key: string, level: number, parentPath: string[] = []): JSX.Element => {
        const isObject = typeof value === "object" && value !== null;
        const isArray = Array.isArray(value);
        const fullPath = [...parentPath, key];
        const pathKey = fullPath.join("__");
        const isCollapsed = collapsedFields[pathKey];
        const hasContent = isObject && Object.keys(value).length > 0;

        if (!matchesSearch(key, value)) return <></>;

        if (isObject && hasContent) {
            const entries = Object.entries(value);
            const itemCount = entries.length;
            if (isArray) { // special rendering for arrays
                return (
                    <div key={pathKey} className={`json-item json-item-array level-${level}`}>
                        <div className="json-item-header" onClick={() => toggleCollapse(pathKey)}>
                            <div className="json-item-header-left">
                                <div className="expand-icon">{isCollapsed ? <BiChevronRight size={18} /> : <BiChevronDown size={18} />}</div>
                                <div className="type-icon-copy-wrapper">
                                    {getTypeIcon(value)}
                                    <div
                                        className="copy-button-overlay"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            copyValueToClipboard(value, key);
                                        }}
                                        title="Copy value"
                                    >
                                        <MdContentCopy size={14} />
                                    </div>
                                </div>
                                <span className="json-key">
                                    {key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                                </span>
                                <span className="json-item-count">[{itemCount}]</span>
                            </div>
                            <div className="json-item-actions">
                                {!isCollapsed && (
                                    <>
                                        <div
                                            className="array-expand-collapse-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                collapseAllArrayItems(fullPath, itemCount);
                                            }}
                                            title="Collapse all items"
                                        >
                                            <BiCollapseVertical size={16} />
                                        </div>
                                        <div
                                            className="array-expand-collapse-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                expandAllArrayItems(fullPath, itemCount);
                                            }}
                                            title="Expand all items"
                                        >
                                            <BiExpandVertical size={16} />
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>
                        <div className={`json-item-content ${isCollapsed ? "collapsed" : "expanded"}`}>
                            <div className="json-array-items">
                                {(value as any[]).map((item, index) => renderArrayItem(item, index, fullPath))}
                            </div>
                        </div>
                    </div>
                );
            }

            // regular object rendering
            return (
                <div key={pathKey} className={`json-item level-${level}`}>
                    <div className="json-item-header" onClick={() => toggleCollapse(pathKey)}>
                        <div className="json-item-header-left">
                            <div className="expand-icon">
                                {isCollapsed ? <BiChevronRight size={18} /> : <BiChevronDown size={18} />}
                            </div>
                            <div className="type-icon-copy-wrapper">
                                {getTypeIcon(value)}
                                <div
                                    className="copy-button-overlay"
                                    title="Copy value"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyValueToClipboard(value, key);
                                    }}
                                >
                                    <MdContentCopy size={14} />
                                </div>
                            </div>
                            <span className="json-key">
                                {key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                            </span>
                            <span className="json-item-count">[{itemCount}]</span>
                            {/* preview - visible when collapsed */}
                            {isCollapsed && (
                                <div className="json-object-preview">
                                    {entries.slice(0, 3).map(([k, v], idx) => {
                                        const previewValue = getPreviewText(v);
                                        return (
                                            <span key={k} className="json-preview-field">
                                                <span className="json-preview-key">{k.replace(/_/g, " ")}:</span>
                                                <span className={`json-preview-value ${typeof v}`}>
                                                    {previewValue.length > 25 ? previewValue.substring(0, 25) + "..." : previewValue}
                                                </span>
                                                {idx < Math.min(entries.length - 1, 2) && <span className="json-preview-separator">•</span>}
                                            </span>
                                        );
                                    })}
                                    {entries.length > 3 && <span className="json-preview-more">+{entries.length - 3} more</span>}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`json-item-content ${isCollapsed ? "collapsed" : "expanded"}`}>
                        <div className="json-item-children">
                            {entries.map(([childKey, childValue]) =>
                                renderValue(childValue, childKey, level + 1, fullPath)
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // primitive value
        return (
            <div
                key={pathKey}
                className={`json-item json-item-primitive level-${level}`}
            >
                <div className="json-primitive-row">
                    <div className="json-primitive-left">
                        <div className="type-icon-copy-wrapper">
                            {getTypeIcon(value)}
                            <div
                                className="copy-button-overlay"
                                onClick={() => copyValueToClipboard(value, key)}
                                title="Copy value"
                            >
                                <MdContentCopy size={14} />
                            </div>
                        </div>
                        <span className="json-key">
                            {key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())}
                        </span>
                    </div>
                    <div className="json-primitive-right">
                        {renderEditableValue(value, pathKey, fullPath)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="json_viewer_interactive_main">
            <div className="json_viewer_interactive_header">
                <div className="json_viewer_interactive_title">{title}</div>

                <div className="json_viewer_interactive_controls">
                    <SearchBox
                        placeholder="Search keys & values..."
                        value={searchTerm}
                        onChange={(_, newValue) => setSearchTerm(newValue || "")}
                        onClear={() => setSearchTerm("")}
                        className="json-search-box"
                        styles={dpaSearchStyles}
                    />
                    <div className="json_viewer_interactive_buttons">
                        <div
                            onClick={expandAll}
                            className="json_viewer_interactive_button"
                            role="button"
                            tabIndex={0}
                            aria-label="Expand all"
                        >
                            <TooltipHost content="Expand all" directionalHint={DirectionalHint.topCenter}>
                                <div className="json_viewer_interactive_icon">
                                    <BiExpandVertical size={20} />
                                </div>
                            </TooltipHost>
                        </div>
                        <div
                            onClick={collapseAll}
                            className="json_viewer_interactive_button"
                            role="button"
                            tabIndex={0}
                            aria-label="Collapse all"
                        >
                            <TooltipHost content="Collapse all" directionalHint={DirectionalHint.topCenter}>
                                <div className="json_viewer_interactive_icon">
                                    <BiCollapseVertical size={20} />
                                </div>
                            </TooltipHost>
                        </div>
                        <div className="button-divider" />
                        <div
                            onClick={() => getSchemeIntoClipboard(displayData)}
                            className="json_viewer_interactive_button"
                            role="button"
                            tabIndex={0}
                            aria-label="Copy content schema"
                        >
                            <TooltipHost content="Copy content schema" directionalHint={DirectionalHint.topCenter}>
                                <div className="json_viewer_interactive_icon">
                                    <TbTableOptions size={18} />
                                </div>
                            </TooltipHost>
                        </div>
                        <div
                            onClick={() => getDataIntoClipboard(displayData)}
                            className="json_viewer_interactive_button"
                            role="button"
                            tabIndex={0}
                            aria-label="Copy content as JSON"
                        >
                            <TooltipHost content="Copy content as JSON" directionalHint={DirectionalHint.topCenter}>
                                <div className="json_viewer_interactive_icon">
                                    <MdContentCopy size={17} />
                                </div>
                            </TooltipHost>
                        </div>
                        <div
                            onClick={() => {
                                try {
                                    downloadJsonAsCsv(displayData, title || "data");
                                    toast.success("CSV downloaded", { autoClose: 1500 });
                                } catch (err) {
                                    console.error("‹errr‾json‾viewer‾csv›", err);
                                    toast.error("Failed to export CSV.");
                                }
                            }}
                            className="json_viewer_interactive_button"
                            role="button"
                            tabIndex={0}
                            aria-label="Download as CSV"
                        >
                            <TooltipHost content="Download as CSV" directionalHint={DirectionalHint.topCenter}>
                                <div className="json_viewer_interactive_icon">
                                    <MdFileDownload size={19} />
                                </div>
                            </TooltipHost>
                        </div>
                        {onApplyFieldOrderToDocType && fieldorder && fieldorder.length > 0 && (sgp?.permissionlevel || 1) >= 2 && (
                            <>
                                <div className="button-divider" />
                                <div
                                    onClick={() => onApplyFieldOrderToDocType(orderedKeys)}
                                    className="json_viewer_interactive_button"
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Preserve Field Order to All Items with Same Type"
                                >
                                    <TooltipHost content="Preserve Field Order to All Items with Same Type" directionalHint={DirectionalHint.topCenter}>
                                        <div className="json_viewer_interactive_icon">
                                            <CiSaveUp1 size={20} />
                                        </div>
                                    </TooltipHost>
                                </div>
                            </>
                        )}
                        {editable && onContentChange && (
                            <>
                                <div className="button-divider" />
                                <div
                                    onClick={() => { setEditModeEnabled(prev => !prev); cancelEdit(); }}
                                    className={`json_viewer_interactive_button ${editModeEnabled ? "jvi-edit-toggle-active" : ""}`}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={editModeEnabled ? "Disable editing" : "Enable editing"}
                                >
                                    <TooltipHost content={editModeEnabled ? "Disable editing" : "Enable editing"} directionalHint={DirectionalHint.topCenter}>
                                        <div className="json_viewer_interactive_icon">
                                            {editModeEnabled ? <MdEditOff size={18} /> : <MdEdit size={18} />}
                                        </div>
                                    </TooltipHost>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="json_viewer_interactive_content">
                <div className="json-viewer-grid">
                    {orderedKeys.map(key => (
                        <div
                            key={key}
                            draggable={!!aiid && !!onFieldOrderChange}
                            onDragStart={(e) => { e.stopPropagation(); handleDragStart(key); }}
                            onDragEnd={(e) => { e.stopPropagation(); handleDragEnd(); }}
                            onDragOver={(e) => { e.stopPropagation(); handleDragOver(e); }}
                            onDrop={(e) => { e.stopPropagation(); handleDrop(key); }}
                            className={draggedKey === key ? "json-item-dragging" : ""}
                        >
                            {renderValue((editableData || displayData)[key], key, 0, [])}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default JsonViewerInteractive;
